import { ApiError } from '@/shared/api/ApiError';

import type { ElementSearchHit, ParsedCommand } from './types';

/**
 * Rozwiązywanie **nazw na identyfikatory** (spec 6.1).
 *
 * Parser zwraca to, co człowiek napisał — czyli **nazwy**: `>Eksport @depends-on Waluta`.
 * Zapis potrzebuje identyfikatorów. Między jednym a drugim stoi krok, którego spec nie
 * pozwala pominąć: *„niejednoznaczne → panel do wyboru"*. Zgadywanie przy dwóch „Eksportach"
 * oznaczałoby krawędź wpiętą w zły element — cicho i bez śladu.
 *
 * ⭐ **Trzy słowniki, jedna reguła rozstrzygania.** Nazwa wskazuje element, byt organizacyjny
 * (usera lub zespół) albo pozycję słownika typów — to trzy różne rodziny (spec sek. 3,
 * decyzja 83), z osobnymi trasami i osobnymi uprawnieniami. Ale pytanie „czy ta nazwa wskazuje
 * dokładnie jedno" jest w każdym przypadku **tym samym pytaniem**, więc odpowiada na nie jeden
 * `resolveOne`. Druga kopia tej reguły rozjechałaby się przy pierwszej poprawce.
 *
 * ⚠ **Typ różni się jedną rzeczą i warto ją nazwać:** rozstrzygnięcie elementu i właściciela
 * daje **identyfikator** potrzebny do zapisu, a rozstrzygnięcie typu daje **kanoniczną nazwę** —
 * bo element trzyma typ nazwą, nie kluczem obcym (decyzja 83). Reguła dopasowania jest ta sama,
 * różny jest tylko plon.
 *
 * Cała logika siedzi tutaj, czysto, bez Reacta: to są reguły produktu (co jest dokładnym
 * trafieniem, kiedy wybór jest konieczny), a nie szczegół widoku.
 */

/** Do czego odnosi się nazwa — decyduje, gdzie jej szukać i jak nazwać ją w panelu. */
export type ReferenceRole = 'subject' | 'target' | 'parent' | 'owner' | 'type';

/**
 * Katalog, w którym szukamy nazwy. Elementy jadą przez szukanie, byty przez `/api/org`,
 * typy przez `/api/element-types`.
 */
export type ReferenceDictionary = 'elements' | 'org' | 'types';

/**
 * Rodzaj bytu organizacyjnego.
 *
 * ⚠ Ten sam alfabet co `OwnerKindDto` i `OrgRefKind` po drucie, ale **celowo własny typ**:
 * oba końce (katalog w `identity`, zapis w `elements`) są związane z dokumentem OpenAPI
 * osobno, a ten moduł tylko przenosi wartość między nimi.
 */
export type OwnerKind = 'user' | 'team';

export interface Reference {
  /** Stabilny klucz pozycji — rola plus indeks, bo ta sama nazwa może wystąpić dwa razy. */
  key: string;
  role: ReferenceRole;
  /** Nazwa dokładnie tak, jak ją napisano. */
  name: string;
}

/** Gdzie szukać nazwy o tej roli. Jedno miejsce, żeby widok i zapytania nie miały własnych zdań. */
export function dictionaryOf(role: ReferenceRole): ReferenceDictionary {
  if (role === 'owner') {
    return 'org';
  }

  if (role === 'type') {
    return 'types';
  }

  return 'elements';
}

/**
 * Kandydat do rozstrzygnięcia — **wspólny mianownik elementu i bytu organizacyjnego**.
 *
 * `title` jest polem wspólnym, bo po nim idzie porównanie z tym, co napisano. Reszta różni
 * się z definicji: element ma identyfikator i typ, byt organizacyjny — rodzaj i identyfikator
 * w swoim katalogu. ⚠ **Osoba i zespół mogą nazywać się tak samo**, więc `ownerKind` nie jest
 * ozdobą listy: bez niego wybór między nimi byłby wyborem między dwoma identycznymi wierszami.
 */
export type ReferenceCandidate =
  | { dictionary: 'elements'; title: string; elementId: number; type: string | null }
  | { dictionary: 'org'; title: string; ownerKind: OwnerKind; ownerId: number }
  | { dictionary: 'types'; title: string; typeId: number };

/**
 * Klucz kandydata — **unikalny w obrębie wszystkich katalogów naraz**.
 *
 * Rodzaj jest w kluczu, bo user 7, zespół 7 i typ 7 to trzy różne byty, a lista pokazuje
 * je w jednym oknie.
 */
export function candidateKey(candidate: ReferenceCandidate): string {
  if (candidate.dictionary === 'elements') {
    return `el-${candidate.elementId}`;
  }

  if (candidate.dictionary === 'types') {
    return `ty-${candidate.typeId}`;
  }

  return `${candidate.ownerKind}-${candidate.ownerId}`;
}

/** Referencja po skonfrontowaniu z katalogiem. */
export type Resolution =
  | { key: string; state: 'resolved'; candidate: ReferenceCandidate }
  | { key: string; state: 'ambiguous'; candidates: readonly ReferenceCandidate[] }
  | { key: string; state: 'missing' };

/** Trafienie szukania jako kandydat — jedyne miejsce, w którym te dwa kształty się spotykają. */
export function candidateOfHit(hit: ElementSearchHit): ReferenceCandidate {
  return {
    dictionary: 'elements',
    title: hit.title,
    elementId: hit.elementId,
    type: hit.type,
  };
}

/**
 * Pozycja słownika typów w postaci, jakiej potrzebuje rozstrzyganie.
 *
 * ⚠ Celowo **własny, minimalny kształt**, a nie `ElementType` z modułu `elements` — ten sam
 * powód co przy `OwnerKind` wyżej: ten moduł tylko przenosi wartość między katalogiem
 * a zapisem, a wiązanie z kontraktem OpenAPI należy do właściciela danych.
 */
export interface ElementTypeEntry {
  id: number;
  name: string;
  isActive: boolean;
}

/**
 * Kandydaci ze słownika typów — **filtruje front, nie serwer**.
 *
 * ⚠ To NIE jest niespójność z elementami, tylko konsekwencja rozmiaru: elementów są tysiące
 * i filtruje je indeks bazy, typów jest kilkanaście i cały słownik i tak siedzi w pamięci
 * podręcznej (ten sam argument, którym panel członkostwa zrezygnował ze stronicowania).
 * Dopasowanie musi tu jednak zostać **takie samo w duchu**: `resolveOne` ma regułę „jeden
 * kandydat rozstrzyga", więc podanie mu całego słownika bez filtra znaczyłoby, że przy
 * jednopozycyjnym słowniku **dowolny wpisany tekst** rozwiązywałby się na tę jedną pozycję.
 *
 * ⛔ Wycofane typy **nie są kandydatami** — serwer i tak odmówi ich nadania
 * (`graph.element.inactive_type`), a oferowanie ich w podpowiedziach byłoby zaproszeniem
 * do odmowy. Do nazwania tej sytuacji służy `withdrawnTypeName`.
 */
export function typeCandidates(
  name: string,
  types: readonly ElementTypeEntry[],
): ReferenceCandidate[] {
  const wanted = normalize(name);

  return types
    .filter((type) => type.isActive && normalize(type.name).includes(wanted))
    .map((type) => ({ dictionary: 'types' as const, title: type.name, typeId: type.id }));
}

/**
 * Czy ta nazwa to **wycofany** typ — i jak brzmi kanonicznie.
 *
 * ⭐ Bez tego rozróżnienia człowiek, który przepisuje typ widziany na starszym elemencie,
 * dostawałby „nie ma takiego typu" — zdanie nieprawdziwe, kierujące go do zakładania nowej
 * pozycji zamiast do przywrócenia istniejącej. Serwer rozróżnia te dwa przypadki dwoma kodami
 * błędu, więc front, który blokuje Enter wcześniej, musi je rozróżniać sam.
 *
 * Dopasowanie **dokładne po normalizacji**, nie fragmentem: to jest nazwanie konkretnego
 * wpisu, a nie szukanie.
 */
export function withdrawnTypeName(
  name: string,
  types: readonly ElementTypeEntry[],
): string | null {
  const wanted = normalize(name);

  return types.find((type) => !type.isActive && normalize(type.name) === wanted)?.name ?? null;
}

/**
 * Nazwy właścicieli obiecane w komendzie — **dwiema drogami, obie ze speca**.
 *
 * ⭐ `>Faktura @owner QA` (spec 6.5) to postać **relacyjna**: wtedy „cele" komendy są bytami
 * organizacyjnymi, a nie elementami. To rozróżnienie jest tu najważniejsze — bez niego „QA"
 * byłoby szukane wśród elementów i przy odrobinie pecha (element o tej nazwie istnieje)
 * powstałaby **krawędź `depends-on` zamiast przypisania właściciela**. Cicho i w grafie.
 *
 * `@owner:A,B` (spec 6.4) to postać **modyfikatora**. Honorujemy ją przy obu komendach
 * tworzących: napisana obietnica, którą program ignoruje, jest gorsza niż odmowa.
 * Przy `!` parser zgłasza problem (właściciel zadania jest dziedziczony z elementu,
 * spec 3.4), więc do wykonania i tak nie dochodzi.
 */
export function ownerNamesOf(command: ParsedCommand): string[] {
  const fromRelation =
    command.kind === 'createRelation' && command.relation === 'owner' ? command.targets : [];

  const fromModifier =
    command.kind === 'createElement' || command.kind === 'createRelation' ? command.owners : [];

  return [...fromRelation, ...fromModifier];
}

/** Które nazwy trzeba rozwiązać, żeby dało się wykonać tę komendę. */
export function referencesOf(command: ParsedCommand): Reference[] {
  const refs: Reference[] = [];

  // Typ idzie PIERWSZY, bo pierwszy stoi w napisanej komendzie (`+@moduł Bramka @in:…`).
  // Kolejność nie jest kosmetyką: po niej poznajemy, którą nazwę człowiek właśnie pisze.
  if (command.kind === 'createElement' && command.type) {
    refs.push({ key: 'type', role: 'type', name: command.type });
  }

  if (command.kind === 'createElement' && command.in) {
    refs.push({ key: 'parent', role: 'parent', name: command.in });
  }

  if (command.kind === 'createRelation' || command.kind === 'createCommitment') {
    if (command.subject) {
      refs.push({ key: 'subject', role: 'subject', name: command.subject });
    }
  }

  // Cele są elementami TYLKO wtedy, gdy relacja łączy elementy. Przy `@owner` ta sama
  // pozycja składni znaczy co innego — patrz `ownerNamesOf`.
  if (command.kind === 'createRelation' && command.relation !== 'owner') {
    command.targets.forEach((target, index) => {
      refs.push({ key: `target-${index}`, role: 'target', name: target });
    });
  }

  ownerNamesOf(command).forEach((owner, index) => {
    refs.push({ key: `owner-${index}`, role: 'owner', name: owner });
  });

  return refs;
}

/**
 * Konfrontuje jedną nazwę z kandydatami z jej katalogu.
 *
 * ⭐ **Dokładne trafienie tytułu wygrywa, nawet gdy trafień jest wiele.** Bez tej reguły
 * `>Płatności` przy istniejących „Płatności" i „Płatności mobilne" zawsze wymagałoby wyboru —
 * a przecież napisano dokładnie jeden z tych tytułów. Porównanie po normalizacji, bo człowiek
 * pisze bez ogonków i wielkich liter równie często jak z nimi.
 */
export function resolveOne(
  reference: Reference,
  candidates: readonly ReferenceCandidate[],
): Resolution {
  if (candidates.length === 0) {
    return { key: reference.key, state: 'missing' };
  }

  const wanted = normalize(reference.name);
  const exact = candidates.filter((candidate) => normalize(candidate.title) === wanted);

  if (exact.length === 1) {
    return { key: reference.key, state: 'resolved', candidate: exact[0]! };
  }

  // Dwa byty o TEJ SAMEJ nazwie to nie jest błąd danych (spec: tytuł nie jest kluczem,
  // a osoba i zespół mogą nazywać się tak samo), więc wybór jest tu jedynym uczciwym
  // wyjściem — nie ma czego preferować.
  if (exact.length > 1) {
    return { key: reference.key, state: 'ambiguous', candidates: exact };
  }

  if (candidates.length === 1) {
    return { key: reference.key, state: 'resolved', candidate: candidates[0]! };
  }

  return { key: reference.key, state: 'ambiguous', candidates };
}

/**
 * Normalizacja do porównywania nazw: małe litery i ścięte ogonki.
 *
 * ⚠ `ł` **nie rozkłada się** przez `NFD` (spec sek. 5) — potrzebna jawna mapa. To ta sama
 * pułapka, którą backend ma w indeksie; tutaj dotyczy wyłącznie sprawdzenia „czy to dokładnie
 * ta nazwa", więc dwie implementacje nie mogą się rozjechać co do wyniku szukania.
 */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replaceAll('ł', 'l')
    .normalize('NFD')
    .replaceAll(/\p{Mn}/gu, '')
    .trim();
}

/** Czy wszystkie nazwy są rozstrzygnięte i można zapisywać. */
export function allResolved(resolutions: readonly Resolution[]): boolean {
  return resolutions.every((r) => r.state === 'resolved');
}

/** Mapa `klucz → identyfikator elementu` dla rozstrzygniętych; wołający wie, że są komplet. */
export function resolvedElementIds(resolutions: readonly Resolution[]): Map<string, number> {
  const map = new Map<string, number>();

  for (const r of resolutions) {
    if (r.state === 'resolved' && r.candidate.dictionary === 'elements') {
      map.set(r.key, r.candidate.elementId);
    }
  }

  return map;
}

/**
 * Kanoniczna nazwa typu po rozstrzygnięciu — to ona idzie do zapisu.
 *
 * ⭐ Nie to samo, co napisano: `+@modul …` zapisze **„Moduł"**. Ma znaczenie, bo selektor
 * kampanii dopasowuje typ **dokładną równością** (decyzja 83) — a serwer i tak
 * kanonizuje, więc rozjazd byłby wyłącznie po stronie tego, co widać przed zapisem.
 */
export function resolvedTypeName(resolutions: readonly Resolution[]): string | null {
  for (const r of resolutions) {
    if (r.state === 'resolved' && r.candidate.dictionary === 'types') {
      return r.candidate.title;
    }
  }

  return null;
}

/** Właściciel gotowy do zapisu. `title` zostaje, bo błąd musi umieć powiedzieć, o kogo chodziło. */
export interface ResolvedOwner {
  ownerKind: OwnerKind;
  ownerId: number;
  title: string;
}

/**
 * Właściciel, o którego potknął się wsad — wyłuskany z `args` odmowy.
 *
 * ⭐ To cała cena za zejście z pętli żądań na jeden wsad (decyzja 97): przypisanie idzie
 * niepodzielnie, a serwer wskazuje **pierwszego** brakującego przez `kind` i `id`. Nazwę
 * dokłada tutaj wołający, z listy, którą sam zbudował — więc komunikat nazywa winowajcę,
 * a API nie dosyła niczego tylko po to, żeby dało się go złożyć (decyzja 96).
 *
 * ⚠ Zwraca `null`, gdy odmowa **nie** dotyczy konkretnego właściciela (brak prawa do edycji,
 * element skasowany w międzyczasie). Wskazanie wtedy pierwszej nazwy z listy byłoby zmyślaniem:
 * człowiek przeczytałby „nie udało się przypisać Ops", choć z Ops wszystko było w porządku.
 */
export function blamedOwner(
  owners: readonly ResolvedOwner[],
  failure: unknown,
): ResolvedOwner | null {
  if (!(failure instanceof ApiError) || failure.code !== 'graph.owner.not_found') {
    return null;
  }

  const kind = failure.args['kind'];
  const id = failure.args['id'];

  if (typeof kind !== 'string' || typeof id !== 'number') {
    return null;
  }

  return owners.find((owner) => owner.ownerKind === kind && owner.ownerId === id) ?? null;
}

/** Rozstrzygnięci właściciele, **w kolejności z komendy**. */
export function resolvedOwners(resolutions: readonly Resolution[]): ResolvedOwner[] {
  const owners: ResolvedOwner[] = [];

  for (const r of resolutions) {
    if (r.state === 'resolved' && r.candidate.dictionary === 'org') {
      owners.push({
        ownerKind: r.candidate.ownerKind,
        ownerId: r.candidate.ownerId,
        title: r.candidate.title,
      });
    }
  }

  return owners;
}
